/**
 * mb-testimonials.js — BASIS. Mango Butter
 * Testimonials slider: vertical image slide + horizontal text slide.
 * Vanilla JS, no dependencies.
 */

(function () {
  'use strict';

  const DURATION      = 560;   // matches --ts-dur in CSS
  const EASE_DELAY    = 180;   // text enter delay (ms)

  function initSlider(root) {
    /* ── DOM refs ── */
    const imgSlides   = root.querySelectorAll('[data-ts-img-slide]');
    const textSlides  = root.querySelectorAll('[data-ts-text-slide]');
    const thumbBtns   = root.querySelectorAll('[data-ts-thumb]');
    const prevBtn     = root.querySelector('[data-ts-prev]');
    const nextBtn     = root.querySelector('[data-ts-next]');
    const counterCur  = root.querySelector('[data-ts-cur]');
    const progressEl  = root.querySelector('[data-ts-progress]');

    if (!imgSlides.length || !textSlides.length) return;

    const total    = imgSlides.length;
    let current    = 0;
    let isAnimating = false;

    /* ── Helpers ── */

    function pad(n) {
      return String(n + 1).padStart(2, '0');
    }

    function updateCounter(idx) {
      if (!counterCur) return;
      counterCur.classList.remove('is-bumping');
      // Force reflow to restart animation
      void counterCur.offsetWidth;
      counterCur.classList.add('is-bumping');
      counterCur.textContent = pad(idx);
    }

    function updateProgress(idx) {
      if (!progressEl) return;
      const pct = ((idx + 1) / total * 100).toFixed(1);
      progressEl.style.width = pct + '%';
    }

    function updateThumbs(idx) {
      thumbBtns.forEach((btn, i) => {
        const active = i === idx;
        btn.classList.toggle('is-active', active);
        btn.setAttribute('aria-selected', active ? 'true' : 'false');
      });
    }

    /* ── Core transition ── */

    function goTo(nextIdx, dir) {
      if (isAnimating || nextIdx === current) return;
      isAnimating = true;

      const curImg  = imgSlides[current];
      const nextImg = imgSlides[nextIdx];
      const curTxt  = textSlides[current];
      const nextTxt = textSlides[nextIdx];

      const exitImgClass  = dir === 'next' ? 'is-exiting-next'  : 'is-exiting-prev';
      const enterImgClass = dir === 'next' ? 'is-entering-next' : 'is-entering-prev';
      const exitTxtClass  = dir === 'next' ? 'is-exiting-next'  : 'is-exiting-prev';
      const enterTxtClass = dir === 'next' ? 'is-entering-next' : 'is-entering-prev';

      /* 1. Trigger exit on current slides */
      curImg.classList.remove('is-current');
      curImg.classList.add(exitImgClass);
      curTxt.classList.remove('is-current');
      curTxt.classList.add(exitTxtClass);
      curTxt.setAttribute('aria-hidden', 'true');

      /* 2. Immediately show next slides with enter animation */
      nextImg.classList.remove('is-hidden');
      nextImg.classList.add(enterImgClass);
      nextImg.removeAttribute('aria-hidden');

      nextTxt.classList.remove('is-hidden');
      nextTxt.classList.add(enterTxtClass);
      nextTxt.removeAttribute('aria-hidden');

      /* 3. Update UI immediately */
      updateCounter(nextIdx);
      updateProgress(nextIdx);
      updateThumbs(nextIdx);

      /* 4. Clean up after animation completes */
      const totalTime = DURATION + EASE_DELAY + 40; // small buffer
      setTimeout(() => {
        // Finalize old slide
        curImg.classList.remove(exitImgClass);
        curImg.classList.add('is-hidden');

        curTxt.classList.remove(exitTxtClass);
        curTxt.classList.add('is-hidden');

        // Finalize new slide
        nextImg.classList.remove(enterImgClass);
        nextImg.classList.add('is-current');

        nextTxt.classList.remove(enterTxtClass);
        nextTxt.classList.add('is-current');

        current     = nextIdx;
        isAnimating = false;
      }, totalTime);
    }

    function goNext() {
      goTo((current + 1) % total, 'next');
    }

    function goPrev() {
      goTo((current - 1 + total) % total, 'prev');
    }

    /* ── Event listeners ── */

    if (nextBtn) nextBtn.addEventListener('click', goNext);
    if (prevBtn) prevBtn.addEventListener('click', goPrev);

    thumbBtns.forEach((btn) => {
      btn.addEventListener('click', () => {
        const idx = parseInt(btn.dataset.tsThumb, 10);
        if (isNaN(idx)) return;
        const dir = idx > current ? 'next' : 'prev';
        goTo(idx, dir);
      });
    });

    /* ── Keyboard navigation ── */
    root.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') { e.preventDefault(); goNext(); }
      if (e.key === 'ArrowLeft')  { e.preventDefault(); goPrev(); }
    });

    /* ── Touch / swipe ── */
    let touchStartX = 0;
    let touchStartY = 0;

    root.addEventListener('touchstart', (e) => {
      touchStartX = e.touches[0].clientX;
      touchStartY = e.touches[0].clientY;
    }, { passive: true });

    root.addEventListener('touchend', (e) => {
      const dx = e.changedTouches[0].clientX - touchStartX;
      const dy = e.changedTouches[0].clientY - touchStartY;
      // Only trigger if horizontal swipe is dominant (> 40px and > vertical)
      if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
        dx < 0 ? goNext() : goPrev();
      }
    }, { passive: true });

    /* ── Auto-play (optional — 7 s interval, pauses on hover/focus) ── */
    let autoTimer = null;

    function startAuto() {
      if (total <= 1) return;
      stopAuto();
      autoTimer = setInterval(goNext, 7000);
    }

    function stopAuto() {
      if (autoTimer) { clearInterval(autoTimer); autoTimer = null; }
    }

    root.addEventListener('mouseenter', stopAuto);
    root.addEventListener('mouseleave', startAuto);
    root.addEventListener('focusin',    stopAuto);
    root.addEventListener('focusout',   startAuto);

    startAuto();

    /* ── Shopify section reload ── */
    document.addEventListener('shopify:section:unload', stopAuto, { once: true });
  }

  /* ── Bootstrap: find all slider instances on the page ── */
  function boot() {
    document.querySelectorAll('[data-mb-tslider]').forEach(initSlider);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', boot);

})();
